import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Users, MessageSquare, Clapperboard, Link2, Loader2, Zap, Plus, Trash2, DollarSign, Ticket, Save, Crown, Copy, Ban, RotateCcw, UserX, Gift, Sparkles, Bug, Check, Image as ImageIcon, FileText, ToggleLeft, Download } from "lucide-react";

// Build a CSV blob from the finance.balance_sheet array and trigger a browser download.
// Excel + Google Sheets recognise this format out-of-the-box.
function downloadBalanceSheetCSV(finance) {
  const month = finance.month;
  const rows = [
    ["User", "Plan", "Messages", "Estimated Cost (USD)", "Revenue (USD)", "Net P&L (USD)"],
    ...finance.balance_sheet.map((u) => [
      u.email,
      u.plan,
      String(u.msgs),
      u.estimated_cost.toFixed(2),
      u.revenue.toFixed(2),
      u.net.toFixed(2),
    ]),
  ];
  // Totals row
  const t = finance.balance_sheet.reduce(
    (acc, u) => ({ msgs: acc.msgs + u.msgs, cost: acc.cost + u.estimated_cost, rev: acc.rev + u.revenue, net: acc.net + u.net }),
    { msgs: 0, cost: 0, rev: 0, net: 0 }
  );
  rows.push(["TOTAL", "", String(t.msgs), t.cost.toFixed(2), t.rev.toFixed(2), t.net.toFixed(2)]);
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }); // BOM → Excel UTF-8
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lumioos-balance-sheet-${month}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const FinanceCard = ({ Icon, label, value, sub, color, trend }) => (
  <div className="border border-zinc-200 bg-white p-5 rounded-lg">
    <Icon className={`w-5 h-5 ${color}`} />
    <div className={`mt-3 text-3xl font-black tracking-tighter ${color}`}>{value}</div>
    <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{label}</div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    {trend !== undefined && (
      <div className={`mt-2 text-[11px] font-bold ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
        {trend >= 0 ? "▲" : "▼"} ${Math.abs(trend).toFixed(2)} vs last month
      </div>
    )}
  </div>
);

// Small inline form for setting the manual Emergent Universal Key balance.
// Admin opens Emergent in another tab, copies the number, pastes it here,
// hits "Save" → backend snapshots {balance, recorded_at}. Future LLM spend is
// subtracted from this to display an "estimated remaining" figure.
// "Auto-calibrate" learns the cost multiplier from observed Emergent drift.
function BalanceInput({ multiplier, onSaved }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoCal, setAutoCal] = useState(true);
  const handleSave = async () => {
    const n = parseFloat(String(val).replace(",", "."));
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a non-negative number");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin/llm-credit/balance", { balance: n, auto_calibrate: autoCal });
      onSaved?.(res.data);
      setVal("");
      const c = res.data?.calibration;
      if (c?.calibrated) {
        toast.success(`Auto-calibrated · multiplier → ${c.new_multiplier.toFixed(2)}x (was estimating ${c.raw_estimate.toFixed(2)}, real drop ${c.actual_drop.toFixed(2)})`);
      } else {
        toast.success("Balance saved · live tracking started");
      }
    } catch (e) {
      toast.error("Could not save balance");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-zinc-200 p-3 min-w-[280px]" data-testid="admin-llm-balance-input-wrap">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[10px] uppercase tracking-widest font-black text-zinc-600">Sync balance from Emergent</div>
        {multiplier != null && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200" title="Cost multiplier — compensates for Emergent's service fee">
            ×{Number(multiplier).toFixed(2)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-zinc-500 font-bold">$</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="12.16"
          className="flex-1 px-2 py-1.5 rounded border border-zinc-300 text-lg font-bold tabular-nums focus:border-zinc-900 focus:outline-none"
          data-testid="admin-llm-balance-input"
        />
        <button
          onClick={handleSave}
          disabled={busy || !val}
          data-testid="admin-llm-balance-save"
          className="px-3 py-1.5 rounded bg-zinc-900 text-white text-xs font-bold disabled:opacity-50 hover:bg-zinc-800 transition-colors"
        >
          {busy ? "..." : "Save"}
        </button>
      </div>
      <label className="mt-2 flex items-center gap-1.5 text-[10.5px] text-zinc-600 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={autoCal}
          onChange={(e) => setAutoCal(e.target.checked)}
          data-testid="admin-llm-balance-auto-cal"
          className="rounded border-zinc-400 w-3.5 h-3.5"
        />
        <span><strong>Auto-calibrate</strong> multiplier from observed drift</span>
      </label>
      <div className="mt-1 text-[10px] text-zinc-500 leading-tight">
        Copy the balance from <strong>Profile → Universal Key</strong>. Re-enter after each top-up — the multiplier auto-tunes to match Emergent's actual deduction.
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [discDraft, setDiscDraft] = useState({ code: "", percent_off: 25, max_uses: 100, note: "" });
  const { t } = useT();
  const [autoMode, setAutoMode] = useState({ enabled: false, mode: "low_only", audit_log: [] });
  const [loading, setLoading] = useState(true);

  // Plan pricing editor
  const [plans, setPlans] = useState([]);
  const [priceDraft, setPriceDraft] = useState({});
  const [savingPrice, setSavingPrice] = useState(null);

  // Referral codes
  const [refs, setRefs] = useState([]);
  const [refDraft, setRefDraft] = useState({ code: "", plan_id: "creator", months_free: 1, max_uses: 10, note: "" });
  const [creatingRef, setCreatingRef] = useState(false);

  // Auto-trial for new signups
  const [autoTrial, setAutoTrial] = useState({ enabled: false, plan_id: "pro", days: 2 });
  const [savingAutoTrial, setSavingAutoTrial] = useState(false);

  // Per-user trial grant (modal-less: small inline draft)
  const [trialDraft, setTrialDraft] = useState({ user_id: null, plan_id: "pro", days: 2 });

  // Bug reports (admin inbox)
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const openReports = reports.filter((r) => r.status !== "resolved");

  // Feature flags — admin can hide a feature entirely or mark it "coming soon"
  // so users see a teaser rather than a working button. Frontend respects
  // these flags everywhere (Sidebar, Dashboard cards, mobile nav).
  const [featureFlags, setFeatureFlags] = useState({});
  const [knownFeatures, setKnownFeatures] = useState([]);
  const [savingFlag, setSavingFlag] = useState(null);

  const loadFeatureFlags = async () => {
    try {
      const { data } = await api.get("/feature-flags");
      setFeatureFlags(data?.flags || {});
      setKnownFeatures(data?.known || []);
    } catch { /* ignore */ }
  };

  const setFeatureFlag = async (key, state) => {
    setSavingFlag(key);
    try {
      const { data } = await api.put("/admin/feature-flags", { key, state });
      setFeatureFlags(data?.flags || data || {});
      toast.success(`${key} → ${state}`);
    } catch {
      toast.error("Failed to update feature flag");
    } finally {
      setSavingFlag(null);
    }
  };

  const loadReports = async () => {
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data || []);
    } catch { /* ignore */ }
  };

  const openReportDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/reports/${id}`);
      setActiveReport(data);
    } catch {
      toast.error("Could not load report");
    }
  };

  const resolveReport = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      toast.success("Report marked as resolved");
      setActiveReport(null);
      loadReports();
      // Tell the Sidebar to refresh its red badge instantly
      window.dispatchEvent(new CustomEvent("lumioos:reports-updated"));
    } catch {
      toast.error("Could not resolve");
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Delete this report permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/reports/${id}`);
      toast.success("Report deleted");
      setActiveReport(null);
      loadReports();
      window.dispatchEvent(new CustomEvent("lumioos:reports-updated"));
    } catch {
      toast.error("Could not delete");
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}),
      api.get("/admin/auto-mode").then((r) => setAutoMode(r.data)).catch(() => {}),
      api.get("/admin/plans").then((r) => {
        setPlans(r.data);
        const d = {};
        r.data.forEach((p) => { d[p.id] = { monthly_price: p.monthly_price, yearly_price: p.yearly_price }; });
        setPriceDraft(d);
      }).catch(() => {}),
      api.get("/admin/referrals").then((r) => setRefs(r.data)).catch(() => {}),
      api.get("/admin/settings/auto-trial").then((r) => setAutoTrial(r.data)).catch(() => {}),
      api.get("/admin/reports").then((r) => setReports(r.data || [])).catch(() => {}),
      loadFeatureFlags(),
    ]).finally(() => setLoading(false));
  }, [user]);

  const createDiscount = async () => {
    if (!discDraft.code.trim()) return toast.error("Code required");
    try {
      const { data } = await api.post("/admin/discounts", {
        code: discDraft.code.trim(),
        percent_off: parseInt(discDraft.percent_off, 10),
        max_uses: parseInt(discDraft.max_uses, 10),
        note: discDraft.note,
      });
      setDiscounts((arr) => [data, ...arr]);
      setDiscDraft({ code: "", percent_off: 25, max_uses: 100, note: "" });
      toast.success("Discount code created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteDiscount = async (code) => {
    if (!window.confirm(`Delete discount code "${code}"?`)) return;
    try {
      await api.delete(`/admin/discounts/${code}`);
      setDiscounts((arr) => arr.filter((d) => d.code !== code));
      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  const saveAutoTrial = async (next) => {
    setSavingAutoTrial(true);
    try {
      await api.post("/admin/settings/auto-trial", next);
      setAutoTrial(next);
      toast.success(next.enabled ? `Auto-trial ON: new signups get ${next.days} days of ${next.plan_id.toUpperCase()}` : "Auto-trial OFF");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingAutoTrial(false);
    }
  };

  const grantTrial = async (target_id) => {
    const { plan_id, days } = trialDraft;
    try {
      const { data } = await api.post(`/admin/users/${target_id}/trial`, { plan_id, days });
      toast.success(`Granted ${days}-day ${plan_id.toUpperCase()} trial — expires ${new Date(data.expires_at).toLocaleDateString()}`);
      // Refresh users list to show updated plan badge
      const r = await api.get("/admin/users");
      setUsers(r.data);
      setTrialDraft({ user_id: null, plan_id: "pro", days: 2 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant trial");
    }
  };

  const updateAutoMode = async (next) => {
    try {
      await api.post("/admin/auto-mode", next);
      setAutoMode((s) => ({ ...s, ...next }));
      toast.success(`Auto Mode: ${next.enabled ? "ON" : "OFF"} (${next.mode})`);
    } catch {
      toast.error("Failed to update Auto Mode");
    }
  };

  const savePrice = async (planId) => {
    setSavingPrice(planId);
    try {
      const body = priceDraft[planId];
      const { data } = await api.patch(`/admin/plans/${planId}`, body);
      setPlans((ps) => ps.map((p) => (p.id === planId ? data : p)));
      toast.success(`${data.name} pricing updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSavingPrice(null);
    }
  };

  const createReferral = async (e) => {
    e.preventDefault();
    if (!refDraft.code.trim()) { toast.error("Code required"); return; }
    setCreatingRef(true);
    try {
      const { data } = await api.post("/admin/referrals", {
        code: refDraft.code.trim().toUpperCase(),
        plan_id: refDraft.plan_id,
        months_free: parseInt(refDraft.months_free, 10) || 1,
        max_uses: parseInt(refDraft.max_uses, 10) || 1,
        note: refDraft.note,
      });
      setRefs((rs) => [data, ...rs]);
      setRefDraft({ code: "", plan_id: "creator", months_free: 1, max_uses: 10, note: "" });
      toast.success(`Code ${data.code} created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create code");
    } finally {
      setCreatingRef(false);
    }
  };

  const deleteReferral = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    try {
      await api.delete(`/admin/referrals/${code}`);
      setRefs((rs) => rs.filter((r) => r.code !== code));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied`);
  };

  const userAction = async (uid, action, extra = {}) => {
    const confirms = {
      delete: "Permanently delete this user and ALL their data? This cannot be undone.",
      suspend: "Suspend this user? They will be locked out until you unsuspend.",
      ban: "BAN this email permanently? Their account + data will be deleted AND they cannot register again with this email until you unban them.",
      make_admin: "Promote this user to ADMIN? They will get full access to this dashboard.",
      revoke_admin: "Remove admin rights from this user?",
    };
    if (confirms[action] && !window.confirm(confirms[action])) return;
    try {
      await api.post(`/admin/users/${uid}/action`, { action, ...extra });
      if (action === "delete" || action === "ban") {
        setUsers((us) => us.filter((u) => u.id !== uid));
        toast.success(action === "ban" ? "User banned (email blacklisted)" : "User deleted");
      } else if (action === "suspend") {
        setUsers((us) => us.map((u) => (u.id === uid ? { ...u, suspended: true } : u)));
        toast.success("User suspended");
      } else if (action === "unsuspend") {
        setUsers((us) => us.map((u) => (u.id === uid ? { ...u, suspended: false } : u)));
        toast.success("User reactivated");
      } else if (action === "make_admin") {
        setUsers((us) => us.map((u) => (u.id === uid ? { ...u, is_admin: true } : u)));
        toast.success("User promoted to admin");
      } else if (action === "revoke_admin") {
        setUsers((us) => us.map((u) => (u.id === uid ? { ...u, is_admin: false } : u)));
        toast.success("Admin rights revoked");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/app" replace />;

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-page">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">Admin</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">Control Center</h1>
      <p className="mt-2 text-zinc-600">Manage pricing, referral codes, auto mode and users.</p>

      {/* Stats */}
      {stats && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border border-zinc-200 bg-white p-5">
            <Users className="w-5 h-5 text-orange-600" />
            <div className="mt-3 text-3xl font-black tracking-tighter">{stats.users}</div>
            <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Users</div>
          </div>
          <div className="border border-zinc-200 bg-white p-5">
            <Link2 className="w-5 h-5 text-orange-600" />
            <div className="mt-3 text-3xl font-black tracking-tighter">{stats.connected_integrations || 0}</div>
            <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Connections</div>
          </div>
          <div className="border border-zinc-200 bg-white p-5">
            <MessageSquare className="w-5 h-5 text-orange-600" />
            <div className="mt-3 text-3xl font-black tracking-tighter">{stats.chats}</div>
            <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Chats</div>
          </div>
          <div className="border border-zinc-200 bg-white p-5">
            <Clapperboard className="w-5 h-5 text-orange-600" />
            <div className="mt-3 text-3xl font-black tracking-tighter">{stats.videos}</div>
            <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Videos</div>
          </div>
        </div>
      )}

      {/* DISCOUNT CODES */}
      <section className="mt-10" data-testid="admin-discounts">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-fuchsia-600" />
          <h2 className="text-xl font-bold tracking-tight">Discount codes</h2>
        </div>
        <p className="mt-1 text-sm text-zinc-600">Create % off codes — share the URL `?code=XYZ` with customers or paste at checkout.</p>

        <div className="mt-4 border border-zinc-200 bg-white p-4 rounded-lg flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Code</label>
            <input
              value={discDraft.code}
              onChange={(e) => setDiscDraft({ ...discDraft, code: e.target.value.toUpperCase() })}
              data-testid="discount-code-input"
              placeholder="LAUNCH25"
              className="w-32 px-3 py-1.5 border border-zinc-200 rounded text-sm font-mono"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">% off</label>
            <input type="number" min="1" max="100"
              value={discDraft.percent_off}
              onChange={(e) => setDiscDraft({ ...discDraft, percent_off: e.target.value })}
              data-testid="discount-percent-input"
              className="w-20 px-3 py-1.5 border border-zinc-200 rounded text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Max uses</label>
            <input type="number" min="1"
              value={discDraft.max_uses}
              onChange={(e) => setDiscDraft({ ...discDraft, max_uses: e.target.value })}
              data-testid="discount-max-input"
              className="w-24 px-3 py-1.5 border border-zinc-200 rounded text-sm"
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[200px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Note (optional)</label>
            <input
              value={discDraft.note}
              onChange={(e) => setDiscDraft({ ...discDraft, note: e.target.value })}
              data-testid="discount-note-input"
              placeholder="Launch campaign"
              className="px-3 py-1.5 border border-zinc-200 rounded text-sm"
            />
          </div>
          <button
            onClick={createDiscount}
            data-testid="discount-create-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white rounded-md font-bold text-sm hover:bg-fuchsia-700"
          >
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>

        {discounts.length > 0 && (
          <div className="mt-4 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left">
                  <th className="py-2 px-3 font-semibold">Code</th>
                  <th className="font-semibold">% Off</th>
                  <th className="font-semibold">Uses</th>
                  <th className="font-semibold">Note</th>
                  <th className="font-semibold text-right pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((d) => {
                  const exhausted = d.uses >= d.max_uses;
                  return (
                    <tr key={d.code} className="border-b border-zinc-100 last:border-0">
                      <td className="py-2 px-3 font-mono font-bold">{d.code}</td>
                      <td className="text-fuchsia-700 font-bold">−{d.percent_off}%</td>
                      <td className="tabular-nums">{d.uses} / {d.max_uses}{exhausted && <span className="ml-1 text-[10px] text-red-600">EXHAUSTED</span>}</td>
                      <td className="text-zinc-500 text-xs">{d.note}</td>
                      <td className="text-right pr-3">
                        <button onClick={() => deleteDiscount(d.code)} className="text-red-600 hover:text-red-800" data-testid={`discount-del-${d.code}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>


      {/* PRICING EDITOR */}
      <section className="mt-10 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/50 p-6 rounded-lg" data-testid="admin-pricing">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-orange-600" />
          <h2 className="text-xl font-bold tracking-tight">Subscription pricing</h2>
        </div>
        <p className="mt-1 text-sm text-zinc-700">Set monthly + yearly prices. Changes apply immediately to all new checkouts.</p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`admin-plan-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold text-lg uppercase tracking-wide">{p.name}</div>
                <span className="text-[10px] font-mono uppercase text-zinc-500">{p.id}</span>
              </div>
              <div className="mt-3 space-y-2">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Monthly $</span>
                  <input
                    type="number" min="0" step="0.5" disabled={p.id === "free"}
                    value={priceDraft[p.id]?.monthly_price ?? p.monthly_price}
                    onChange={(e) => setPriceDraft((d) => ({ ...d, [p.id]: { ...d[p.id], monthly_price: e.target.value } }))}
                    data-testid={`admin-price-monthly-${p.id}`}
                    className="mt-1 w-full px-3 py-1.5 border border-zinc-300 rounded text-sm disabled:bg-zinc-100"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Yearly $</span>
                  <input
                    type="number" min="0" step="1" disabled={p.id === "free"}
                    value={priceDraft[p.id]?.yearly_price ?? p.yearly_price}
                    onChange={(e) => setPriceDraft((d) => ({ ...d, [p.id]: { ...d[p.id], yearly_price: e.target.value } }))}
                    data-testid={`admin-price-yearly-${p.id}`}
                    className="mt-1 w-full px-3 py-1.5 border border-zinc-300 rounded text-sm disabled:bg-zinc-100"
                  />
                </label>
              </div>
              <button
                onClick={() => savePrice(p.id)} disabled={p.id === "free" || savingPrice === p.id}
                data-testid={`admin-price-save-${p.id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
                {savingPrice === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* REFERRAL CODES */}
      <section className="mt-10 border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-amber-50/50 p-6 rounded-lg" data-testid="admin-referrals">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-pink-600" />
          <h2 className="text-xl font-bold tracking-tight">Referral codes</h2>
        </div>
        <p className="mt-1 text-sm text-zinc-700">Generate codes to give users free months of any plan. Share the code — they redeem it from Billing → &quot;Have a code?&quot;.</p>

        <form onSubmit={createReferral} className="mt-5 bg-white border border-zinc-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="admin-ref-form">
          <label className="md:col-span-2 block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Code</span>
            <input value={refDraft.code} onChange={(e) => setRefDraft((d) => ({ ...d, code: e.target.value }))}
              placeholder="WELCOME50" maxLength={64} data-testid="admin-ref-code"
              className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded text-sm font-mono uppercase" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Plan</span>
            <select value={refDraft.plan_id} onChange={(e) => setRefDraft((d) => ({ ...d, plan_id: e.target.value }))}
              data-testid="admin-ref-plan"
              className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded text-sm">
              <option value="creator">Creator</option>
              <option value="pro">Pro</option>
              <option value="power">Power</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Months free</span>
            <input type="number" min="1" max="24" value={refDraft.months_free}
              onChange={(e) => setRefDraft((d) => ({ ...d, months_free: e.target.value }))}
              data-testid="admin-ref-months"
              className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Max uses</span>
            <input type="number" min="1" max="100000" value={refDraft.max_uses}
              onChange={(e) => setRefDraft((d) => ({ ...d, max_uses: e.target.value }))}
              data-testid="admin-ref-uses"
              className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded text-sm" />
          </label>
          <button type="submit" disabled={creatingRef} data-testid="admin-ref-create"
            className="md:col-span-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50">
            {creatingRef ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
          </button>
          <label className="md:col-span-6 block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Note (private)</span>
            <input value={refDraft.note} onChange={(e) => setRefDraft((d) => ({ ...d, note: e.target.value }))}
              placeholder="e.g. Friend of Alber"
              className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded text-sm" />
          </label>
        </form>

        <div className="mt-5">
          {refs.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-6 bg-white border border-dashed border-zinc-300 rounded-md">No codes yet. Create one above.</div>
          ) : (
            <ul className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-200" data-testid="admin-ref-list">
              {refs.map((r) => (
                <li key={r.code} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`admin-ref-row-${r.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-pink-700">{r.code}</code>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-bold">
                        {r.plan_id} · {r.months_free}mo
                      </span>
                      <span className="text-xs text-zinc-500">{r.used_count}/{r.max_uses} used</span>
                    </div>
                    {r.note && <div className="text-xs text-zinc-500 mt-0.5 truncate">{r.note}</div>}
                  </div>
                  <button onClick={() => copyCode(r.code)} className="p-1.5 hover:bg-zinc-100 rounded text-zinc-600" title="Copy">
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteReferral(r.code)} data-testid={`admin-ref-delete-${r.code}`}
                    className="p-1.5 hover:bg-red-50 rounded text-zinc-600 hover:text-red-600" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Auto Mode (admin-only) */}
      <div className="mt-10 border-2 border-amber-300 bg-amber-50 p-6 rounded-lg" data-testid="admin-auto-mode">
        <div className="flex items-start gap-3">
          <Zap className={`w-6 h-6 mt-0.5 ${autoMode.enabled ? "text-amber-600" : "text-zinc-400"}`} />
          <div className="flex-1">
            <h2 className="text-xl font-bold tracking-tight">Auto Mode <span className="text-xs font-semibold uppercase ml-2 text-amber-700">Admin only</span></h2>
            <p className="text-sm text-zinc-700 mt-1">When enabled, Lumioo processes cases without manual approval.</p>
          </div>
          <button onClick={() => updateAutoMode({ enabled: !autoMode.enabled, mode: autoMode.mode })}
            data-testid="admin-automode-toggle"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider ${
              autoMode.enabled ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-white border border-zinc-300 hover:bg-zinc-50"
            }`}>
            {autoMode.enabled ? "ON" : "OFF"}
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "notify_only", label: "Notify only", desc: "AI prepares reply, you approve" },
            { key: "low_only", label: "Low priority only", desc: "Auto-solve Low / Medium; flag High / Critical" },
            { key: "all", label: "All cases (risky)", desc: "AI solves & closes everything. Audit log only." },
          ].map((m) => (
            <button key={m.key} onClick={() => updateAutoMode({ enabled: autoMode.enabled, mode: m.key })}
              data-testid={`admin-automode-${m.key}`} disabled={!autoMode.enabled}
              className={`text-left p-3 border rounded-md transition-colors disabled:opacity-50 ${
                autoMode.mode === m.key ? "border-amber-600 bg-white" : "border-zinc-200 bg-white hover:border-zinc-400"
              }`}>
              <div className="text-sm font-bold">{m.label}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Users list */}
      <div className="mt-10">
        <h2 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-orange-600" /> All users ({users.length})</h2>

        {/* Auto-trial for new signups */}
        <div className="mb-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-md p-5" data-testid="auto-trial-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="text-base font-black tracking-tight flex items-center gap-2"><Gift className="w-5 h-5 text-purple-600" /> Free trial for new signups</h3>
              <p className="text-sm text-zinc-700 mt-1">
                When <strong>ON</strong>: every new user gets the chosen plan free for N days, then auto-rolls back to Free. The
                <span className="mx-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600"><Sparkles className="w-2.5 h-2.5" />{autoTrial.days}-day trial</span>
                badge auto-appears on Landing, Sign-up and Pricing pages, plus a countdown banner inside the app.
              </p>
              <p className="text-sm text-zinc-700 mt-2">
                When <strong>OFF</strong>: new signups land on Free plan and <strong>every trial advert across the site disappears automatically</strong> — no manual cleanup.
              </p>
              <div className="mt-3 p-3 bg-white border border-purple-200 rounded text-xs">
                <div className="font-bold uppercase tracking-wider text-purple-700 mb-1">Current state</div>
                {autoTrial.enabled ? (
                  <span data-testid="auto-trial-current-state">
                    ✅ Trial is <strong>LIVE</strong> — new users get <strong>{autoTrial.days} days of {autoTrial.plan_id.toUpperCase()}</strong> free. Ads visible everywhere.
                  </span>
                ) : (
                  <span data-testid="auto-trial-current-state">
                    ⛔ Trial is <strong>OFF</strong> — new users land on Free. All trial ads hidden.
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <select
                  value={autoTrial.plan_id}
                  onChange={(e) => setAutoTrial((s) => ({ ...s, plan_id: e.target.value }))}
                  className="text-sm px-3 py-2 border border-purple-300 rounded bg-white"
                  data-testid="auto-trial-plan"
                >
                  <option value="pro">Pro</option>
                  <option value="creator">Creator</option>
                  <option value="power">Power</option>
                </select>
                <span className="text-sm text-zinc-700">for</span>
                <input
                  type="number" min={1} max={365}
                  value={autoTrial.days}
                  onChange={(e) => setAutoTrial((s) => ({ ...s, days: parseInt(e.target.value, 10) || 1 }))}
                  className="text-sm px-3 py-2 border border-purple-300 rounded w-24 bg-white"
                  data-testid="auto-trial-days"
                />
                <span className="text-sm text-zinc-700">days</span>
                <button
                  onClick={() => saveAutoTrial({ ...autoTrial, enabled: !autoTrial.enabled })}
                  disabled={savingAutoTrial}
                  className={`ml-2 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded ${autoTrial.enabled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-zinc-800 hover:bg-zinc-900 text-white"}`}
                  data-testid="auto-trial-toggle"
                >
                  {savingAutoTrial ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {autoTrial.enabled ? "ON" : "OFF"}
                </button>
                {autoTrial.enabled && (
                  <button
                    onClick={() => saveAutoTrial(autoTrial)}
                    disabled={savingAutoTrial}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold border border-purple-400 text-purple-700 hover:bg-purple-100 rounded"
                    data-testid="auto-trial-save"
                  >
                    Save changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-200 max-h-[500px] overflow-y-auto" data-testid="admin-users-list">
          {users.map((u) => (
            <div key={u.id}>
            <div className={`px-4 py-3 flex items-center justify-between gap-3 ${u.suspended ? "bg-red-50/60" : ""}`} data-testid={`admin-user-${u.email}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{u.email}</span>
                  {u.is_owner && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white rounded inline-flex items-center gap-1 shadow-sm" title="Site owner — immutable"><Crown className="w-2.5 h-2.5" /> OWNER</span>}
                  {u.is_admin && !u.is_owner && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-pink-500 text-white rounded inline-flex items-center gap-1"><Crown className="w-2.5 h-2.5" /> ADM</span>}
                  {u.plan && u.plan !== "free" && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{u.plan}</span>}
                  {u.suspended && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-600 text-white rounded inline-flex items-center gap-1"><Ban className="w-2.5 h-2.5" /> Suspended</span>}
                </div>
                <div className="text-xs text-zinc-500 truncate">{u.name}</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono shrink-0 hidden md:block">{new Date(u.created_at).toLocaleDateString()}</div>
              {u.id !== user.id && !u.is_owner && (
                <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                  {!u.is_admin && (
                    <button
                      onClick={() => setTrialDraft({ user_id: u.id, plan_id: "pro", days: 2 })}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-md"
                      data-testid={`admin-trial-${u.email}`}
                      title={u.plan_expires_at ? `Current trial expires ${new Date(u.plan_expires_at).toLocaleDateString()} — click to extend` : "Grant trial"}
                    >
                      <Gift className="w-3.5 h-3.5" /> Trial
                    </button>
                  )}
                  {u.is_admin ? (
                    <button
                      onClick={() => userAction(u.id, "revoke_admin")}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-md"
                      data-testid={`admin-revoke-admin-${u.email}`}
                      title="Remove admin rights"
                    >
                      <Crown className="w-3.5 h-3.5" /> Revoke admin
                    </button>
                  ) : (
                    <button
                      onClick={() => userAction(u.id, "make_admin")}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold border border-amber-400 text-amber-700 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 rounded-md"
                      data-testid={`admin-make-admin-${u.email}`}
                      title="Promote to admin"
                    >
                      <Crown className="w-3.5 h-3.5" /> Make admin
                    </button>
                  )}
                  {!u.is_admin && (u.suspended ? (
                    <button
                      onClick={() => userAction(u.id, "unsuspend")}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-md"
                      data-testid={`admin-unsuspend-${u.email}`}
                      title="Reactivate user"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Unsuspend
                    </button>
                  ) : (
                    <button
                      onClick={() => userAction(u.id, "suspend")}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-md"
                      data-testid={`admin-suspend-${u.email}`}
                      title="Lock user out (reversible)"
                    >
                      <Ban className="w-3.5 h-3.5" /> Suspend
                    </button>
                  ))}
                  {!u.is_admin && (
                    <button
                      onClick={() => userAction(u.id, "delete")}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 rounded-md"
                      data-testid={`admin-delete-${u.email}`}
                      title="Delete account + data (email CAN re-register)"
                    >
                      <UserX className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                  {!u.is_admin && (
                    <button
                      onClick={() => userAction(u.id, "ban", { reason: window.prompt("Ban reason? (optional)") || "" })}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold border border-red-500 text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm"
                      data-testid={`admin-ban-${u.email}`}
                      title="Delete + blacklist email (cannot re-register)"
                    >
                      <UserX className="w-3.5 h-3.5" /> Ban
                    </button>
                  )}
                </div>
              )}
            </div>
            {trialDraft.user_id === u.id && (
              <div className="px-4 py-3 bg-purple-50 border-t border-purple-200 flex items-center gap-2 flex-wrap" data-testid={`trial-draft-${u.email}`}>
                <span className="text-xs font-semibold text-purple-800">Grant trial:</span>
                <select
                  value={trialDraft.plan_id}
                  onChange={(e) => setTrialDraft((d) => ({ ...d, plan_id: e.target.value }))}
                  className="text-xs px-2 py-1.5 border border-purple-300 rounded bg-white"
                  data-testid={`trial-plan-${u.email}`}
                >
                  <option value="pro">Pro</option>
                  <option value="creator">Creator</option>
                  <option value="power">Power</option>
                </select>
                <input
                  type="number" min={1} max={365}
                  value={trialDraft.days}
                  onChange={(e) => setTrialDraft((d) => ({ ...d, days: parseInt(e.target.value, 10) || 1 }))}
                  className="text-xs px-2 py-1.5 border border-purple-300 rounded w-20 bg-white"
                  data-testid={`trial-days-${u.email}`}
                />
                <span className="text-xs text-purple-700">days</span>
                <button
                  onClick={() => grantTrial(u.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded"
                  data-testid={`trial-grant-confirm-${u.email}`}
                >
                  <Sparkles className="w-3.5 h-3.5" /> Grant
                </button>
                <button
                  onClick={() => setTrialDraft({ user_id: null, plan_id: "pro", days: 2 })}
                  className="text-xs px-2 py-1.5 text-purple-700 hover:underline"
                >
                  Cancel
                </button>
              </div>
            )}
            </div>
          ))}
        </div>
      </div>

      {/* Feature flags — admin can hide buttons or mark as "coming soon" */}
      <div className="mt-12" data-testid="admin-feature-flags-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <ToggleLeft className="w-5 h-5 text-blue-600" /> Navigation & Feature buttons
          </h2>
          <span className="text-xs text-zinc-500">Hide a feature or mark it Coming Soon — applies to every user instantly.</span>
        </div>
        <div className="border border-zinc-200 bg-white rounded-md divide-y divide-zinc-200">
          {knownFeatures.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">Loading…</div>
          ) : knownFeatures.map((f) => {
            const state = featureFlags[f.key] || "enabled";
            const isBusy = savingFlag === f.key;
            return (
              <div key={f.key} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`flag-row-${f.key}`}>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-zinc-900">{f.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    <code className="font-mono">{f.key}</code>
                    <span className="mx-2">·</span>
                    Current state: <span className={`font-semibold ${
                      state === "enabled" ? "text-emerald-600"
                      : state === "new" ? "text-emerald-500"
                      : state === "coming_soon" ? "text-amber-600"
                      : "text-red-600"
                    }`}>{state}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {["enabled", "new", "coming_soon", "hidden"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFeatureFlag(f.key, s)}
                      disabled={isBusy || state === s}
                      data-testid={`flag-${f.key}-${s}`}
                      className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors border ${
                        state === s
                          ? s === "new"
                            ? "bg-emerald-500 text-white border-emerald-500 cursor-default"
                            : "bg-zinc-900 text-white border-zinc-900 cursor-default"
                          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900 hover:text-zinc-900"
                      } ${isBusy ? "opacity-50 cursor-wait" : ""}`}
                    >
                      {isBusy && state !== s ? <Loader2 className="w-3 h-3 animate-spin" /> : s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bug Reports inbox */}
      <div className="mt-12" data-testid="admin-reports-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Bug className="w-5 h-5 text-orange-600" /> Bug reports
            {openReports.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-black bg-red-600 text-white rounded-full" data-testid="admin-reports-badge">
                {openReports.length}
              </span>
            )}
          </h2>
          <button onClick={loadReports} className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="admin-reports-refresh">
            <RotateCcw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {reports.length === 0 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-8 text-center text-sm text-zinc-500">
            No reports yet. When users send bug reports they&apos;ll appear here.
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-200 max-h-[500px] overflow-y-auto" data-testid="admin-reports-list">
            {reports.map((r) => (
              <div
                key={r.id}
                className={`w-full px-4 py-3 hover:bg-zinc-50 flex items-start gap-3 ${r.status === "resolved" ? "opacity-60" : ""}`}
                data-testid={`admin-report-${r.id}`}
              >
                <button
                  onClick={() => openReportDetail(r.id)}
                  className="flex-1 text-left flex items-start gap-3 min-w-0"
                  data-testid={`admin-report-open-${r.id}`}
                >
                  <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${r.status === "resolved" ? "bg-emerald-500" : "bg-red-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{r.user_email}</span>
                      {r.attachment_name && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-zinc-100 rounded text-zinc-600">{r.attachment_mime?.includes("pdf") ? "PDF" : "IMG"}</span>}
                      {r.status === "resolved" && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">Resolved</span>}
                    </div>
                    <p className="mt-1 text-sm text-zinc-700 line-clamp-2">{r.message}</p>
                    <div className="mt-1 text-xs text-zinc-400">{new Date(r.created_at).toLocaleString()} · {r.page_url || ""}</div>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }}
                  className="shrink-0 p-2 text-zinc-400 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete report"
                  data-testid={`admin-report-delete-${r.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeReport && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActiveReport(null)}
          data-testid="admin-report-detail"
        >
          <div className="bg-white border-2 border-zinc-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="text-lg font-black tracking-tight flex items-center gap-2"><Bug className="w-5 h-5 text-orange-600" /> Report detail</h2>
              <button onClick={() => setActiveReport(null)} className="p-1 hover:bg-zinc-100 rounded"><Trash2 className="w-4 h-4 rotate-45" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="text-sm space-y-1">
                <div><span className="font-bold">From:</span> {activeReport.user_name} &lt;{activeReport.user_email}&gt;</div>
                <div><span className="font-bold">When:</span> {new Date(activeReport.created_at).toLocaleString()}</div>
                <div className="break-all"><span className="font-bold">Page:</span> {activeReport.page_url || "—"}</div>
                <div className="break-all text-xs text-zinc-500"><span className="font-bold">User-agent:</span> {activeReport.user_agent || "—"}</div>
              </div>
              <div className="bg-zinc-50 border-l-4 border-zinc-900 p-4 text-sm whitespace-pre-wrap" data-testid="admin-report-message">
                {activeReport.message}
              </div>
              {activeReport.attachment_base64 && (
                <div className="border border-zinc-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
                    {activeReport.attachment_mime?.includes("pdf") ? <FileText className="w-3.5 h-3.5" /> : <ImageIcon className="w-3.5 h-3.5" />}
                    {activeReport.attachment_name || "Attachment"}
                  </div>
                  {activeReport.attachment_mime?.startsWith("image/") ? (
                    <img src={`data:${activeReport.attachment_mime};base64,${activeReport.attachment_base64}`} alt="" className="max-w-full rounded" data-testid="admin-report-attachment-image" />
                  ) : (
                    <a
                      href={`data:${activeReport.attachment_mime};base64,${activeReport.attachment_base64}`}
                      download={activeReport.attachment_name}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-900 text-white rounded text-sm font-bold"
                      data-testid="admin-report-attachment-download"
                    >
                      Download {activeReport.attachment_name}
                    </a>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                {activeReport.status !== "resolved" && (
                  <button
                    onClick={() => resolveReport(activeReport.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded"
                    data-testid="admin-report-resolve"
                  >
                    <Check className="w-4 h-4" /> Mark resolved
                  </button>
                )}
                <a
                  href={`mailto:${activeReport.user_email}?subject=Re: your Lumioos bug report`}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:bg-zinc-50 font-bold rounded text-sm"
                >
                  Reply by email
                </a>
                <button
                  onClick={() => deleteReport(activeReport.id)}
                  className="ml-auto inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 font-bold rounded text-sm"
                  data-testid="admin-report-delete"
                  title="Permanently delete this report"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
